import styled from '@emotion/styled';
import {
  Button,
  Drawer,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Tooltip,
  notification,
} from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FaLock, FaLockOpen, FaPen, FaUser, FaUserCheck } from 'react-icons/fa';
import { FiRefreshCcw } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { PulseLoader } from 'react-spinners';
import { AgenceEntity } from '../../../../entities/agence.entity';
import { ConnectedUserEntity } from '../../../../entities/ConnectedUserEntity';
import { ProfilEntity } from '../../../../entities/profil.entity';
import { UserEntity } from '../../../../entities/utilisateur.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { Layout } from '../../../shared/Layout';
import { KYCUserForm } from '../../emissionBond/components/KYCUserForm';
import { ProfilForm } from '../components/ProfilForm';
import { UserForm } from '../components/UserForm';
import {
  deverouillerCompte,
  getUtilisateurDetails,
  verouillerCompte,
} from '../network';

const Container = styled.div`
  height: calc(100vh - 30px);
  display: flex;
  background-color: #1c2025;
  color: white;

  .bread-crum span {
    color: white;
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: #757575;
    color: white;
  }

  .even-row {
    background-color: #acacac;
    color: white;
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: ${YELLOW} !important;
      cursor: pointer;
      color: black; /* plus lisible */
    }
  }

  .content {
    padding: 50px;
    width: 100%;
    background-color: #1c2025;
    color: white;
  }
`;

export const GestionUserProfilPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [agence, setAgence] = useState<AgenceEntity>();
  const [showProfilDrawer, setShowProfilDrawer] = useState(false);
  const [showUpdateUserDrawer, setShowUpdateUserDrawer] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [filterUsers, setFilterUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserEntity>();
  const [selectedUserPro, setSelectedUserPro] = useState<ProfilEntity[]>([]);
  const [textDisplay, setTextDisplay] = useState('');
  const [toActivate, setToActivate] = useState(false);
  const [showDrawer1, setShowDrawer1] = useState(false);

  const connectedUser: ConnectedUserEntity = useSelector(
    (state: any) => state.userReducer,
  ).user;

  const fetchData = () => {
    setIsLoading(true);
    getUtilisateurDetails(connectedUser.id).then(({ data }) => {
      setUsers(data);
      setFilterUsers(data);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeAccountStatus = () => {
    if (selectedUser) {
      if (toActivate) {
        deverouillerCompte(selectedUser.url).then(({ data }) => {
          notification.success({
            message: 'Succès',
            description: 'Le compte a été dévéroullé avec succès',
          });
          setUsers(
            users.map((u) => {
              if (u.id === data.id) {
                u.actif = true;
                return u;
              } else {
                return u;
              }
            }),
          );
          setFilterUsers(
            filterUsers.map((u) => {
              if (u.id === data.id) {
                u.actif = true;
                return u;
              } else {
                return u;
              }
            }),
          );
          setIsLoading(false);
          setShowModal(false);
        });
      } else {
        verouillerCompte(selectedUser.url).then(({ data }) => {
          notification.success({
            message: 'Succès',
            description: 'Le compte a été véroullé avec succès',
          });
          setUsers(
            users.map((u) => {
              if (u.id === data.id) {
                u.actif = false;
                return u;
              } else {
                return u;
              }
            }),
          );
          setFilterUsers(
            filterUsers.map((u) => {
              if (u.id === data.id) {
                u.actif = false;
                return u;
              } else {
                return u;
              }
            }),
          );
          setIsLoading(false);
          setShowModal(false);
        });
      }
    }
  };

  const setColor = (code: string) => {
    switch (code) {
      case 'admin':
        return '#f50';
      case 'resp_commercial':
        return '#3c096c';
      case 'commercial':
        return '#f9a825';
      case 'responsable':
        return '#108ee9';
      case 'chef_agence':
        return '#8d6e63';
      case 'gestionnaire':
        return '#5f0f40';
      case 'sadmin':
        return '#fb8b24';
    }
  };

  const COLUMNS = [
    {
      title: 'Nom',
      dataIndex: 'nom',
      flex: 1,
    },
    {
      title: 'Prénom',
      dataIndex: 'prenom',
      flex: 1,
    },
    // {
    //   title: 'Email',
    //   dataIndex: 'email',
    //   flex: 1,
    // },
    {
      title: 'Adresse',
      dataIndex: 'adresse',
      flex: 1,
    },
    {
      title: 'Contact',
      dataIndex: 'mobile',
      flex: 1,
    },
    {
      title: 'Agence',
      render: (cell: any, row: any) => row.agence?.nom,
      flex: 1,
    },
    {
      title: 'Partenaire',
      render: (cell: any, row: any) => row.partner?.nom,
      flex: 1,
    },
    {
      title: 'Date création',
      dataIndex: 'created_at',
      render: (cell: any) => `${moment(cell).format('DD/MM/YYYY')}`,
      flex: 1,
    },
    {
      title: 'Profils',
      render: (cell: any, row: any) => {
        return (
          <Space>
            {row.profils.length === 0 ? (
              <Tag>--</Tag>
            ) : (
              row.profils.map((pro: any) => (
                <Tag key={pro.id} color={setColor(pro.code_profil)}>
                  {pro.nom}
                </Tag>
              ))
            )}
          </Space>
        );
      },
      flex: 2,
    },
    {
      title: 'Statut',
      render: (cell: any, row: any) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          {row.actif ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'green',
              }}
            ></div>
          ) : (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                backgroundColor: 'red',
              }}
            ></div>
          )}
        </div>
      ),
      flex: 1,
    },
    {
      title: 'Action',
      render: (cell: any, row: any) => (
        <Space>
          <Button
            icon={<FaPen style={{ color: 'white', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: 'white',
            }}
            onClick={() => {
              setSelectedUser(row);
              setShowUpdateUserDrawer(true);
              setAgence(row.agence);
            }}
          />
          <Button
            icon={<FaUser style={{ color: 'white', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: 'white',
            }}
            onClick={() => {
              setSelectedUserPro(row.profils);
              setSelectedUser(row);
              setShowProfilDrawer(true);
            }}
          />
          <Button
            icon={<FaUserCheck style={{ color: 'white', marginBottom: -1 }} />}
            style={{
              backgroundColor: 'transparent',
              borderRadius: 20,
              zoom: 0.8,
              borderColor: 'white',
            }}
            onClick={() => {
              setSelectedUser(row);
              setShowDrawer1(true);
            }}
          />
          {row.actif ? (
            <Button
              icon={<FaLockOpen style={{ color: 'white', marginBottom: -2 }} />}
              style={{
                backgroundColor: 'transparent',
                borderRadius: 20,
                zoom: 0.8,
                borderColor: 'white',
              }}
              onClick={() => {
                setSelectedUser(row);
                setTextDisplay(
                  'Voulez-vous désactiver le compte de cet utilisateur ?',
                );
                setToActivate(false);
                setShowModal(true);
              }}
            />
          ) : (
            <Button
              icon={<FaLock style={{ color: 'white', marginBottom: -2 }} />}
              style={{
                backgroundColor: 'transparent',
                borderRadius: 20,
                zoom: 0.8,
                borderColor: 'white',
              }}
              onClick={() => {
                setSelectedUser(row);
                setTextDisplay(
                  'Voulez-vous réactiver le compte de cet utilisateur ?',
                );
                setToActivate(true);
                setShowModal(true);
              }}
            />
          )}
        </Space>
      ),
      flex: 1,
    },
  ];

  return (
    <Layout>
      <Container>
        <div className='content'>
          <div style={{ marginTop: 0 }}>
            <h2 style={{ color: 'white' }}>
              Gestion des utilisateurs et des profils
            </h2>

            <div style={{ marginBottom: 5 }}>
              <Input
                placeholder='Search...'
                style={{ width: 250, marginRight: 5 }}
                onChange={(e) => {
                  setFilterUsers(
                    users.filter(
                      (u) =>
                        u.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.prenom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.agence?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.partner?.nom
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()) ||
                        u.adresse
                          .toLowerCase()
                          .includes(e.target.value.toLowerCase()),
                    ),
                  );
                }}
              />
              <Tooltip title='Rafraichir' placement='top' color={YELLOW}>
                <span style={{ cursor: 'pointer' }} onClick={fetchData}>
                  <FiRefreshCcw color='black' />
                </span>
              </Tooltip>
              <PulseLoader
                size={5}
                margin={2}
                color={YELLOW}
                loading={isLoading}
              />
            </div>

            <Table
              columns={COLUMNS}
              dataSource={filterUsers}
              rowClassName={(row, index) =>
                index % 2 === 0 ? 'even-row' : 'odd-row'
              }
              size='small'
              style={{ zoom: 0.9 }}
              pagination={false}
              loading={isLoading}
            />
          </div>
        </div>
        <Drawer
          title='Gestion des profils'
          placement='right'
          width={400}
          onClose={() => setShowProfilDrawer(false)}
          open={showProfilDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <ProfilForm user={selectedUser} userProfils={selectedUserPro} />
        </Drawer>
        <Drawer
          title="Mettre à jour l'utilisateur"
          placement='right'
          width={400}
          onClose={() => setShowUpdateUserDrawer(false)}
          open={showUpdateUserDrawer}
          closeIcon={<MdClose size={20} />}
        >
          <UserForm
            userToUpdate={selectedUser}
            agence={agence as AgenceEntity}
            setShowDrawer={setShowUpdateUserDrawer}
            getNewUser={(user) => {}}
          />
        </Drawer>
        <Modal
          className='dark-modal'
          open={showModal}
          title='Confirmation'
          onCancel={() => setShowModal(false)}
          footer={[
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={() => changeAccountStatus()}
                style={{ borderRadius: 10 }}
              >
                Oui
              </Button>
              <Button
                className='butt'
                style={{ borderRadius: 10 }}
                onClick={() => setShowModal(false)}
              >
                Non
              </Button>
            </div>,
          ]}
        >
          <p>{textDisplay} </p>
        </Modal>
        <Drawer
          title='Verifier le KYC'
          placement='right'
          width={400}
          onClose={() => setShowDrawer1(false)}
          open={showDrawer1}
          closeIcon={<MdClose size={20} />}
        >
          <KYCUserForm
            userToUpdate={selectedUser}
            setShowDrawer={setShowDrawer1}
            getNewClient={(cli) => {
              setUsers([...users, cli]);
              setFilterUsers([...users, cli]);
            }}
          />
        </Drawer>
      </Container>
    </Layout>
  );
};
